// @givmo/app-admin — the approvals queue's search / filter / pagination helpers.
//
// Pure and side-effect-free, so the rules that decide WHICH requests an admin is looking at are
// unit-testable away from the component. All of it runs CLIENT-side over the rows the dashboard read
// already returned — the wire has no filtered/searchable approvals endpoint, and inventing query
// params the backend does not honour would silently show a filtered page as if it were the whole set.
//
// PAGINATION IS OVER DISPLAY ROWS, NEVER LEGS. A grouped (ETF/fundraiser) request is ONE row by the
// time it reaches here (`toApprovalRows` collapsed its legs), so slicing a page can never split a
// group across a page boundary — which would render a partial group and, far worse, offer an approve
// over an incomplete `match_ids` set that the backend hard-400s. The page boundary is the row
// boundary; that is the whole reason grouping happens before paging and not after.

import type { ApprovalStatus, MatchApprovalRow } from '../dashboard/dashboard-decode';

/** Rows per page in the queue. */
export const APPROVALS_PAGE_SIZE = 20;

/** The request-shape filter: one charity, or a grouped ETF/fundraiser request. */
export type ApprovalKindFilter = 'single' | 'grouped';

/** The queue's filter/search state. Empty sets mean "no narrowing" — never "match nothing". */
export interface ApprovalFilters {
  /** Free text matched against the employee and the charity/group names. */
  search: string;
  /**
   * Selected statuses; empty = every status. An `Unknown`-status row matches no chip, so it shows
   * only while no status is selected — never under a chip that would misname it.
   */
  statuses: ApprovalStatus[];
  /** Selected request shapes; empty = both. */
  kinds: ApprovalKindFilter[];
}

export const EMPTY_APPROVAL_FILTERS: ApprovalFilters = { search: '', statuses: [], kinds: [] };

/**
 * The statuses offered as filter chips, in queue order (what needs a decision comes first). Cancelled
 * is its own chip, never folded into Rejected: nobody decided a cancelled request (CAM-R33).
 */
export const APPROVAL_STATUS_FILTERS: readonly ApprovalStatus[] = [
  'Pending',
  'Approved',
  'Rejected',
  'Cancelled',
];

/**
 * Chips shown only while the queue holds such a request, or while selected (a selected chip never
 * vanishes from under the admin). Cancelled is an exception, not an outcome every queue accrues, so
 * a company that has never had one keeps exactly the toolbar it had — and so does every environment
 * until the backend starts emitting the status (CAM-R36). A fourth always-on chip also pushed
 * Approve-all onto its own row at 1280px.
 */
const CHIP_SHOWN_WHEN_PRESENT: ReadonlySet<ApprovalStatus> = new Set<ApprovalStatus>(['Cancelled']);

/** The status chips to render, in {@link APPROVAL_STATUS_FILTERS} order. */
export function visibleStatusFilters(
  counts: ReadonlyMap<ApprovalStatus, number>,
  selected: readonly ApprovalStatus[],
): ApprovalStatus[] {
  return APPROVAL_STATUS_FILTERS.filter(
    (status) =>
      !CHIP_SHOWN_WHEN_PRESENT.has(status) ||
      (counts.get(status) ?? 0) > 0 ||
      selected.includes(status),
  );
}

/** Whether the admin has narrowed the queue at all — drives which empty state the table shows. */
export function hasActiveFilters(filters: ApprovalFilters): boolean {
  return (
    filters.search.trim().length > 0 || filters.statuses.length > 0 || filters.kinds.length > 0
  );
}

/**
 * Every name a row can be found by: the employee, plus the charity for a single request or the group
 * name AND each member charity for a grouped one. Member charities are searchable because an admin
 * looking for "did we match Ocean Cleanup?" should find the ETF request that funded it — the same
 * reason the group's composition is disclosed rather than hidden.
 *
 * An employee who hid their name is found by the words that stand for them — "An anonymous employee"
 * (EP-WEB39), as on the screen — and never by their own name: the row carries none (the server sends
 * no `user` for such a request, and the decode reads the flags before any name).
 */
export function searchableNames(row: MatchApprovalRow): string[] {
  if (row.kind === 'grouped') {
    return [
      row.employeeName,
      ...(row.groupName !== null ? [row.groupName] : []),
      ...row.members.map((m) => m.charityName),
    ];
  }
  return [row.employeeName, row.charity.name];
}

/** Case-insensitive substring match over {@link searchableNames}. A blank query matches everything. */
export function matchesSearch(row: MatchApprovalRow, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (needle.length === 0) return true;
  return searchableNames(row).some((name) => name.toLowerCase().includes(needle));
}

/**
 * Apply search + status + kind narrowing, preserving the backend's row order (pending first). Each
 * facet is independent and an empty facet is a no-op, so the default state returns `rows` untouched —
 * which is what keeps the queue's landing view the company's full match history rather than a
 * pending-only slice.
 */
export function filterApprovalRows(
  rows: MatchApprovalRow[],
  filters: ApprovalFilters,
): MatchApprovalRow[] {
  return rows.filter(
    (row) =>
      (filters.statuses.length === 0 || filters.statuses.includes(row.status)) &&
      (filters.kinds.length === 0 || filters.kinds.includes(row.kind)) &&
      matchesSearch(row, filters.search),
  );
}

/** Total pages for a row count (always ≥ 1, so an empty queue is page 1 of 1, never page 1 of 0). */
export function pageCount(total: number, pageSize: number = APPROVALS_PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/**
 * Clamp a 1-based page into range. Filters shrink the result set under a page the admin was already
 * on, so the page number must be clamped at read time rather than trusted — an out-of-range page
 * would otherwise render an empty table over a non-empty result set.
 */
export function clampPage(page: number, total: number, pageSize: number = APPROVALS_PAGE_SIZE) {
  if (!Number.isFinite(page)) return 1;
  return Math.min(Math.max(1, Math.trunc(page)), pageCount(total, pageSize));
}

/** The rows on a 1-based page. Never splits a grouped row — see the pagination note at the top. */
export function pageRows(
  rows: MatchApprovalRow[],
  page: number,
  pageSize: number = APPROVALS_PAGE_SIZE,
): MatchApprovalRow[] {
  const safe = clampPage(page, rows.length, pageSize);
  const start = (safe - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}

/**
 * The footer's range line. States its own basis: `truncated` means the backend cut the activity feed
 * at its page boundary, so `total` is "the most recent N", not the company's whole history — the
 * honesty rule applies to a count as much as to a dollar figure.
 *
 * The qualifier survives FILTERING for the same reason it exists unfiltered: a search runs over the
 * rows this page holds, so on a truncated feed "Showing 2 matching requests" would present a partial
 * search as a definitive one — the admin who searched an employee and saw two hits would conclude
 * there are two, not two in the recent window.
 */
export function rangeLabel(params: {
  total: number;
  page: number;
  pageSize?: number;
  truncated: boolean;
  filtered: boolean;
}): string {
  const { total, page, pageSize = APPROVALS_PAGE_SIZE, truncated, filtered } = params;
  const noun = truncated ? 'most recent requests' : 'requests';
  if (total <= pageSize) {
    if (filtered) {
      const unit = total === 1 ? 'request' : 'requests';
      return truncated
        ? `Showing ${total} matching ${unit} in the most recent history`
        : `Showing ${total} matching ${unit}`;
    }
    return truncated
      ? `Showing the ${total} most recent requests`
      : `Showing all ${total} requests`;
  }
  const safe = clampPage(page, total, pageSize);
  const start = (safe - 1) * pageSize + 1;
  const end = Math.min(safe * pageSize, total);
  return `Showing ${start}–${end} of ${total} ${noun}`;
}
